import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPADataTable from '../shared/SPADataTable';
import SPAPill from '../shared/SPAPill';
import SPASectionHeading from '../shared/SPASectionHeading';
import { GradeSyncLink } from './gradeSyncPath';
import type { CopiesPayload } from './useAssignmentSyncState';

type Copy = CopiesPayload['copies'][number];

const column = createColumnHelper<Copy>();

const courseName = (added: NonNullable<Copy['added_in']>) => added.course_title ?? `LMS course ${added.course_id}`;

// A copy links on its first open from its course. One added in a course and never opened there already has
// a column waiting; the picker finds that course from the same deep link and links it without a launch.
const NeverLinked = ({ copy, pageId }: { copy: Copy; pageId: number | undefined }) => (
  <span>
    <SPAPill tone="warning">Never linked</SPAPill>
    {copy.added_in != null && (
      <span className="tw-mt-1 tw-block tw-text-sm tw-text-spa-secondary">
        {`${copy.added_in.certain ? 'Added in' : 'Probably added in'} ${courseName(copy.added_in)}, never opened there`}
      </span>
    )}
    <GradeSyncLink
      className="tw-mt-1 tw-block tw-text-sm"
      to={`/assignments/${pageId}/copies/${copy.id}/columns?tab=copies`}
    >
      Link to its column
    </GradeSyncLink>
  </span>
);

const columns = [
  column.accessor('id', {
    cell: (info) => {
      const copy = info.row.original;

      return (
        <span>
          {copy.current ? <span className="tw-font-semibold">{copy.id}</span>
            : <GradeSyncLink to={`/assignments/${copy.id}`}>{copy.id}</GradeSyncLink>}
          {copy.title != null && <span className="tw-block tw-text-spa-primary">{copy.title}</span>}
          {copy.current && <span className="tw-block tw-text-sm tw-text-spa-tertiary">this one</span>}
        </span>
      );
    },
    header: 'Assignment',
  }),
  column.accessor('created_at', {
    cell: info => new Date(info.getValue()).toLocaleDateString(),
    header: 'Created',
  }),
  column.accessor(copy => copy.linked_courses?.map(course => course.title ?? course.id).join(', ')
    || copy.course_title || copy.courses.join(', '), {
    cell: (info) => {
      const copy = info.row.original;

      if (!copy.linked) {
        return <NeverLinked copy={copy} pageId={info.table.options.data.find(row => row.current)?.id} />;
      }

      const linked = copy.linked_courses ?? [];
      if (linked.length > 0) {
        return linked.map(course => (
          <span key={course.id} className="tw-block">{course.title ?? `LMS course ${course.id}`}</span>
        ));
      }

      return info.getValue() || <span className="tw-text-spa-tertiary">Linked, never opened</span>;
    },
    header: 'LMS courses it posts grades into',
    id: 'course',
  }),
  column.accessor(copy => copy.opened_by.reduce((sum, room) => sum + room.students, 0), {
    cell: (info) => {
      const rooms = info.row.original.opened_by;

      if (rooms.length === 0) return <span className="tw-text-spa-tertiary">No student</span>;

      return rooms.map(room => (
        <span key={room.id} className="tw-block">
          {room.name ?? `Class ${room.id}`}
          <span className="tw-text-spa-tertiary">
            {` · ${room.students} ${room.students === 1 ? 'student' : 'students'}`}
          </span>
        </span>
      ));
    },
    header: 'TCI classes that opened it',
    id: 'opened_by',
  }),
  column.accessor('teacher_opens', { header: 'Teacher opens', meta: { align: 'right' } }),
  column.accessor('delivered', { header: 'Scores delivered', meta: { align: 'right' } }),
] as ColumnDef<Copy, unknown>[];

// AIDEV-NOTE: The per-copy diagnosis cannot see the whole: a copy linked to one of six courses looks broken
// on its own even when each course has its own working copy. Side by side, the real gap shows — the
// classes no copy has been opened from, whose grades have nowhere to go.
const CopiesPanel = ({ copies, unopened }: { copies: Copy[]; unopened: CopiesPayload['unopened_classes'] }) => {
  const waiting = copies.flatMap(copy => (!copy.linked && copy.added_in != null
    ? [`${copy.id} in ${courseName(copy.added_in)}`]
    : []));

  return (
    <div className="tw-mb-6">
      <SPASectionHeading title="Copies of this content" />
      {unopened.length > 0 && (
        <p className="tw-mb-3 tw-mt-0 tw-text-sm tw-text-spa-error">
          {`No copy has been opened from ${unopened.map(room => room.name ?? room.id).join(', ')}, `}
          so those students&apos; grades have nowhere to go.
          {waiting.length > 0 && (
            <span className="tw-block tw-text-spa-secondary">
              {`Added but never opened: ${waiting.join(', ')}`}
              {' — link each to its column below.'}
            </span>
          )}
        </p>
      )}
      <SPADataTable columns={columns} data={copies} defaultSort={[]} />
    </div>
  );
};

export default CopiesPanel;
